export default function Privacy() {
  return (
    <div className="px-6 py-20">
      <div className="mx-auto max-w-4xl">
        {/* Header */}
        <div className="text-center mb-16 px-4">
          <h1 className="text-4xl sm:text-5xl font-bold text-white tracking-tight">Privacy Policy</h1>
          <p className="mt-4 text-slate-400">Last updated: June 8, 2026</p>
        </div>

        {/* TL;DR Summary */}
        <div className="glass-elevated rounded-3xl p-8 mb-12 border-brand-400/20">
          <h2 className="text-2xl font-bold text-white mb-4">TL;DR — Your Privacy Matters</h2>
          <p className="text-lg text-slate-300 leading-relaxed">
            SwiftDev Toolkit is <strong className="text-white">100% local-first</strong>. We don't collect, transmit, or store any of your code, build data, or personal information on external servers. Everything stays on your Mac.
          </p>
        </div>

        {/* Sections */}
        <div className="space-y-6">
          <div className="glass-card rounded-3xl p-8">
            <h2 className="text-2xl font-semibold text-white mb-4">Overview</h2>
            <p className="text-slate-300 leading-relaxed">
              SwiftDev Toolkit is a native macOS application. Your privacy is important to us. This policy explains what information we collect (spoiler: almost nothing) and how we use it (spoiler: we don't).
            </p>
          </div>

          <div className="glass-card rounded-3xl p-8">
            <h2 className="text-2xl font-semibold text-white mb-4">Information We Do Not Collect</h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              SwiftDev Toolkit does not collect, transmit, or store any of the following on external servers:
            </p>
            <ul className="space-y-2 text-slate-300">
              <li>• Your source code</li>
              <li>• Build artifacts or compiled binaries</li>
              <li>• Error messages or build logs</li>
              <li>• Project names or file paths</li>
              <li>• Personal data or identifiable information</li>
              <li>• Usage analytics or telemetry</li>
            </ul>
            <p className="text-slate-300 leading-relaxed mt-4">
              All project analysis, build execution, and data processing happens <strong className="text-white">locally on your Mac</strong>.
            </p>
          </div>

          <div className="glass-card rounded-3xl p-8">
            <h2 className="text-2xl font-semibold text-white mb-4">Local Data Storage</h2>
            <p className="text-slate-300 leading-relaxed">
              The app stores minimal preferences locally on your device:
            </p>
            <ul className="space-y-2 text-slate-300 mt-4">
              <li>• Last opened project</li>
              <li>• Window preferences</li>
              <li>• Interface settings</li>
            </ul>
            <p className="text-slate-300 leading-relaxed mt-4">
              This data is <strong className="text-white">never transmitted</strong> off your device.
            </p>
          </div>

          <div className="glass-card rounded-3xl p-8">
            <h2 className="text-2xl font-semibold text-white mb-4">Build Reports</h2>
            <p className="text-slate-300 leading-relaxed">
              Build reports are saved to your project directory as local files. You have complete control — delete, share, or version-control them as you see fit.
            </p>
          </div>

          <div className="glass-card rounded-3xl p-8">
            <h2 className="text-2xl font-semibold text-white mb-4">In-App Purchases (StoreKit)</h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              SwiftDev Toolkit uses Apple's StoreKit framework for optional premium feature subscriptions. When you make a purchase:
            </p>
            <ul className="space-y-2 text-slate-300">
              <li>• Apple processes the transaction (we never see your payment information)</li>
              <li>• Your purchase is tied to your Apple ID</li>
              <li>• Apple shares minimal transaction data with us (purchase status, not personal details)</li>
              <li>• All payment data is handled by Apple according to their Privacy Policy</li>
            </ul>
            <p className="text-slate-300 leading-relaxed mt-4">
              We only receive confirmation that a subscription is active — no credit card numbers, billing addresses, or personal information.
            </p>
          </div>

          <div className="glass-card rounded-3xl p-8">
            <h2 className="text-2xl font-semibold text-white mb-4">Third-Party Services</h2>
            <p className="text-slate-300 leading-relaxed">
              SwiftDev Toolkit does not integrate with any third-party analytics, crash reporting, or tracking services. The app does not make network requests except for StoreKit purchases and optional update checks (user-initiated).
            </p>
          </div>

          <div className="glass-card rounded-3xl p-8">
            <h2 className="text-2xl font-semibold text-white mb-4">Changes to This Policy</h2>
            <p className="text-slate-300 leading-relaxed">
              We may update this privacy policy from time to time. Any changes will be posted on this page with an updated "Last updated" date. Continued use of the app after changes constitutes acceptance of the updated policy.
            </p>
          </div>

          <div className="glass-card rounded-3xl p-8">
            <h2 className="text-2xl font-semibold text-white mb-4">Contact</h2>
            <p className="text-slate-300 leading-relaxed">
              If you have questions about this privacy policy, contact us at{' '}
              <a 
                href="mailto:hello@swiftdevtoolkit.com" 
                className="text-brand-400 hover:text-brand-300 transition-colors font-medium hover:underline"
              >
                hello@swiftdevtoolkit.com
              </a>.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
